import axios from 'axios';

/** Typed wrapper for successful API responses */
export interface ApiResponse<T = unknown> {
  data: T;
  message?: string;
}

/** Typed error shape returned by the Rudi backend */
export interface ApiError {
  error: string;
  message: string;
  statusCode: number;
  code?: string;
  detail?: string;
}

/** Error shape for rate-limited (429) responses */
export type RateLimitPayload = {
  retryAfter?: number | string;
  message?: string;
};

/** Enhanced Error with HTTP metadata, used by interceptors */
export type EnhancedError = Error & {
  status?: number;
  retryAfter?: number | string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  response?: { status?: number; data?: unknown; headers?: any };
};

/**
 * Extract a human-readable error message from any error shape.
 * Works with Axios errors, plain Error objects, and unknown values.
 */
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const apiError = error.response?.data as ApiError | undefined;
    if (typeof apiError?.detail === 'string' && apiError.detail.trim()) {
      return apiError.detail;
    }
    if (typeof apiError?.message === 'string' && apiError.message.trim()) {
      return apiError.message;
    }
    if (typeof apiError?.error === 'string' && apiError.error.trim()) {
      return apiError.error;
    }
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

/** Shape used to detect session expiration from various error formats */
interface SessionErrorShape {
  response?: {
    status?: number;
    data?: {
      code?: string;
      message?: string;
    };
  };
  status?: number;
  code?: string;
  message?: string;
}

/**
 * Check if an error represents a session expiration (HTTP 440 or SESSION_EXPIRED code).
 */
export function isSessionExpiredError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;

  const err = error as SessionErrorShape;

  if (err.response?.status === 440) return true;
  if (err.status === 440) return true;
  if (err.response?.data?.code === 'SESSION_EXPIRED') return true;
  if (err.code === 'SESSION_EXPIRED') return true;
  if (err.response?.data?.message && err.response.data.message.includes('SESSION_EXPIRED'))
    return true;
  if (err.message && err.message.includes('SESSION_EXPIRED')) return true;

  return false;
}
